import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Search,
  Filter,
  Eye,
  XCircle,
  CheckCircle2,
  RefreshCw,
  Clock,
  MapPin,
  X,
  CreditCard,
  ShieldCheck,
  ShieldAlert,
  Lock,
  ArrowRight
} from 'lucide-react';
import { AdminBookingItem } from '../../../types/admin.types';
import {
  fetchAdminBookings,
  cancelAdminBookingApi,
} from '../../../lib/adminService';

interface BookingsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export const BookingsTab: React.FC<BookingsTabProps> = ({ onShowNotice }) => {
  const [bookings, setBookings] = useState<AdminBookingItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<AdminBookingItem | null>(null);

  // Cancel Booking modal state
  const [cancelModalBooking, setCancelModalBooking] = useState<AdminBookingItem | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminBookings(statusFilter);
      setBookings(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load bookings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [statusFilter]);

  const handleConfirmCancel = async () => {
    if (!cancelModalBooking) return;
    if (!cancelReason.trim()) {
      onShowNotice('Cancellation reason is strictly required', 'error');
      return;
    }

    setIsSubmittingAction(true);
    try {
      await cancelAdminBookingApi(cancelModalBooking.id, cancelReason);
      onShowNotice(`Booking ${cancelModalBooking.id} cancelled. Payout release blocked.`, 'success');
      setCancelModalBooking(null);
      setCancelReason('');
      loadBookings();
    } catch (err: any) {
      onShowNotice(err.message || 'Error cancelling booking', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Booking Management</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
              {bookings.length} Records
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 13, 14 & 15: State transitions, 15% platform fee split, and meeting completion OTP status.
          </p>
        </div>

        {/* Filter and Refresh */}
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Statuses</option>
            <option value="REQUESTED">Requested / Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="ONGOING">Ongoing</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="REJECTED">Rejected</option>
            <option value="DISPUTED">Disputed</option>
          </select>

          <button
            type="button"
            onClick={loadBookings}
            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Completion OTP Protection Banner (Section 16) */}
      <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-300">
          <Lock className="w-4 h-4 text-purple-400 shrink-0" />
          <span>Section 16 OTP Guard: Admin can view OTP verification state, but never plaintext client OTP codes.</span>
        </div>
        <span className="text-[11px] font-mono font-bold text-emerald-400">Zero-Plaintext Policy Active</span>
      </div>

      {/* Bookings Table (Section 13) */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Booking ID</th>
                <th className="py-3 px-4">Client</th>
                <th className="py-3 px-4">Companion</th>
                <th className="py-3 px-4">Date & Time</th>
                <th className="py-3 px-4">Gross Amount</th>
                <th className="py-3 px-4">Platform Fee (15%)</th>
                <th className="py-3 px-4">Net Payout</th>
                <th className="py-3 px-4">Payment</th>
                <th className="py-3 px-4">Completion OTP</th>
                <th className="py-3 px-4">Booking Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-slate-500">
                    No bookings found matching filter.
                  </td>
                </tr>
              ) : (
                bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-purple-400">{b.id}</td>
                    <td className="py-3.5 px-4 font-bold text-white">{b.clientName}</td>
                    <td className="py-3.5 px-4 font-bold text-purple-300">{b.companionName}</td>
                    <td className="py-3.5 px-4">
                      <div>{b.date}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{b.time}</div>
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-white">
                      ₹{b.bookingAmount.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-purple-400">
                      ₹{b.platformFee.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-emerald-400">
                      ₹{b.netPayout.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          b.paymentStatus === 'PAID'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        }`}
                      >
                        {b.paymentStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          b.otpStatus === 'VERIFIED'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : b.otpStatus === 'ISSUED'
                            ? 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                            : 'bg-slate-800 text-slate-400 border-slate-700'
                        }`}
                      >
                        {b.otpStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          b.bookingStatus === 'COMPLETED'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : b.bookingStatus === 'ONGOING'
                            ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                            : b.bookingStatus === 'CONFIRMED' || b.bookingStatus === 'ACCEPTED'
                            ? 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                            : b.bookingStatus === 'REQUESTED' || b.bookingStatus === 'PENDING'
                            ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                            : 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                        }`}
                      >
                        {b.bookingStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedBooking(b)}
                          className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition cursor-pointer"
                          title="Inspect Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>

                        {b.bookingStatus !== 'COMPLETED' && b.bookingStatus !== 'CANCELLED' && (
                          <button
                            type="button"
                            onClick={() => {
                              setCancelModalBooking(b);
                              setCancelReason('');
                            }}
                            className="p-1.5 bg-rose-950/80 hover:bg-rose-900 border border-rose-800 text-rose-300 rounded-lg transition cursor-pointer"
                            title="Cancel Booking"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Booking Details Modal (Section 14) */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-xl w-full space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                <h3 className="text-base font-black text-white">Booking Details: {selectedBooking.id}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedBooking(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Client</span>
                <span className="font-bold text-white">{selectedBooking.clientName}</span>
                <span className="text-[10px] text-purple-400 block font-mono">{selectedBooking.clientId}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Companion</span>
                <span className="font-bold text-white">{selectedBooking.companionName}</span>
                <span className="text-[10px] text-purple-400 block font-mono">{selectedBooking.companionId}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Schedule & Time</span>
                <span className="font-bold text-slate-200">{selectedBooking.date} • {selectedBooking.time}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Meeting Location</span>
                <span className="text-slate-200">{selectedBooking.location}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Fee Breakdown</span>
                <div className="font-mono text-slate-300">
                  Gross: ₹{selectedBooking.bookingAmount} | Fee: ₹{selectedBooking.platformFee} (15%) | Net: ₹{selectedBooking.netPayout}
                </div>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Completion OTP Status</span>
                <span className="font-bold text-emerald-400 font-mono">{selectedBooking.otpStatus}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Service &amp; Duration</span>
                <span className="font-bold text-slate-200">
                  {selectedBooking.service || '—'}
                  {selectedBooking.durationHours ? ` • ${selectedBooking.durationHours}h` : ''}
                </span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Booking &amp; Payment Status</span>
                <span className="font-bold text-slate-200 font-mono">
                  {selectedBooking.bookingStatus} • {selectedBooking.paymentStatus}
                </span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 col-span-2">
                <span className="text-slate-500 block mb-1">Gateway References</span>
                <div className="font-mono text-[11px] text-slate-300 break-all">
                  Order: {selectedBooking.orderId || '—'} | Payment: {selectedBooking.paymentId || '—'}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedBooking(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Booking Cancellation */}
      {cancelModalBooking && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-rose-900/60 rounded-3xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
              <ShieldAlert className="w-5 h-5" />
              <span>Cancel Booking: {cancelModalBooking.id}</span>
            </div>
            <p className="text-xs text-slate-300">
              Cancelling will update booking status to <strong>CANCELLED</strong> and block companion payout release.
            </p>
            <div>
              <label className="block text-[11px] font-bold text-slate-400 mb-1">
                Mandatory Reason for Cancellation: *
              </label>
              <textarea
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Weather emergency, mutual agreement..."
                className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-rose-500"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmittingAction}
                onClick={() => setCancelModalBooking(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Go Back
              </button>
              <button
                type="button"
                disabled={isSubmittingAction || !cancelReason.trim()}
                onClick={handleConfirmCancel}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold cursor-pointer disabled:opacity-50"
              >
                {isSubmittingAction ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
